import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import cookies from 'nookies';

import {
    getOptimizelyOverrides,
    OptimizelyOverrides
} from '../optimizelyOverrides';

type ExtendedUrlSearchParams = URLSearchParams & {
    entries: () => Iterable<[string, string]>;
};

/**
 * Overrides are passed: pageProps -> app -> Optimizely Provider.
 * For SSR pages these overrides will be generated from the request context.
 * For static pages these overrides will be generated by _middleware and contain
 * canonical flag data as well as the forced user overrides. In both cases we
 * only want to be able to distinguish the genuine user set overrides, the
 * easiest way is to lazily re-inspect the query and cookies in browser.
 */
export const useUserSetOptimizelyOverrides = () => {
    const [overrides, setOverrides] = useState<OptimizelyOverrides>();
    const router = useRouter();
    const path = router && router.asPath;

    useEffect(() => {
        let query = {};
        if (path) {
            const { search } = new URL(
                path,
                // does not need to be dynamic as not relevant to return search val
                'https://www.zoopla.co.uk'
            );
            query = iterableToObject(
                new URLSearchParams(search) as ExtendedUrlSearchParams
            );
        }
        const clientOnlyCookies = cookies.get({});

        const overrides = getOptimizelyOverrides(clientOnlyCookies, query);
        setOverrides(overrides);
    }, [path]);

    return overrides;
};

function iterableToObject(params: ExtendedUrlSearchParams) {
    const result: Record<string, string> = {};
    for (const [key, value] of params.entries()) {
        result[key] = value;
    }
    return result;
}
